// Libraries
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// MUI
import AddIcon from '@mui/icons-material/Add'
import { Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
// Types
import {
	handleTimesheetServicesDetails,
	KmsVehicle,
	ServiceExecution,
	ServiceOriginal,
	Services,
} from '../../../../../store/slices/timesheet/timesheetPageSlice'
// Hooks
import { useIsTimesheetApproved } from '../../../../../utils/hooks'
// Store
import { TimesheetStoreDispatch, TimesheetStoreState } from '../../../../../store/timesheetStore'
import { hasChanges } from '../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalHasChangesSlice'
import { acceptApprovalServices } from '../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
// Components
import KmsVehicleRow from '../../../../KmsVehicleRow'
import CustomButton from '../../../../v2/general/CustomButton/CustomButton'
import CustomTextField from '../../../../general/CustomTextField/CustomTextField'
import PresentationalNotesAndComments from '../../../../PresentationalNotesAndComments'

// Styles
const headerTableCellStyle = {
	fontFamily: 'var(--font-family)',
	fontSize: '0.75rem',
	fontWeight: 600,
	lineHeight: '200%',
	letterSpacing: '0.17px',
	color: 'var(--primary-light)',
}
const bodyTableCellStyle = {
	fontFamily: 'var(--font-family)',
	fontSize: '0.75rem',
	fontWeight: 400,
	lineHeight: '130%',
	border: 'none',
	color: 'var(--primary-dark)',
}

interface ServiceDetailsProps {
	serviceDetail: Services
	instance: number
}

export default function ServiceDetails({ serviceDetail, instance }: Readonly<ServiceDetailsProps>): React.JSX.Element {
	// Local state
	const [kmsVehicles, setKmsVehicles] = useState<KmsVehicle[]>([])

	// Global state and hooks
	const dispatch = useDispatch<TimesheetStoreDispatch>()
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const isTimeSheetApproved = useIsTimesheetApproved()
	const timesheetApprovalDetailsAccept = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
	)

	// Memoized values
	const serviceNotes = useMemo(() => {
		return serviceDetail.serviceExecution?.comments?.trim() ?? ''
	}, [serviceDetail.serviceExecution])
	const isTowDriversService = useMemo(
		() => serviceDetail.mainSecundaryDriver !== null,
		[serviceDetail.mainSecundaryDriver],
	)
	const isSecondaryDriverService = useMemo(
		() => serviceDetail.service.seconddriver ?? false,
		[serviceDetail.service.seconddriver],
	)
	const kmsEmpty = useMemo(() => {
		return (
			kmsVehicles
				?.filter(kmv => kmv.inEmpty)
				.reduce((total, kmv) => total + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0) ?? 0
		)
	}, [kmsVehicles])
	const kmsNotEmpty = useMemo(() => {
		return (
			kmsVehicles
				?.filter(kmv => !kmv.inEmpty)
				.reduce((total, kmv) => total + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0) ?? 0
		)
	}, [kmsVehicles])
	const specialInstructions = useMemo(() => {
		return (
			serviceDetail.service.comments
				?.trim()
				.split('\r\n')
				.filter(excerpt => !excerpt.startsWith('Justificação'))
				.join(' / ') ?? ''
		)
	}, [serviceDetail.service.comments])

	const capitalizeComment = (comment: string) => {
		const s = comment.trim()
		return s ? s[0].toLocaleUpperCase() + s.slice(1) : ''
	}

	// Handlers
	/**
	 * Handler to add a new KmsVehicle entry. It updates both the local state and the global state in Redux.
	 * When adding a new entry, it initializes kmsIni to the last kmsEnd value (or 0 if there are no entries),
	 * kmsEnd to 0, and inEmpty to false. The kmsId is set to a negative value based on the current length of kmsVehicles
	 * to ensure uniqueness for new entries that haven't been saved to the backend yet.
	 */
	const handleAddKmsVehicle = () => {
		if (kmsVehicles !== null) {
			setKmsVehicles(prevKmsVehicles => [
				...prevKmsVehicles!,
				{
					kmsIni: prevKmsVehicles![prevKmsVehicles!.length - 1]?.kmsEnd || 0,
					kmsEnd: 0,
					inEmpty: false,
					kmsId: prevKmsVehicles!.length * -1,
				},
			])

			if (isTimeSheetApproved) {
				const copyOfServices = [...timesheetApprovalDetailsAccept.services]
				for (let i = 0; i < copyOfServices.length; i++) {
					if (i === instance) {
						const prevService = timesheetApprovalDetailsAccept.services[i]
						const prevKmsVehicles = prevService?.kmsVehicles || []
						const updatedKmsVehicles = [
							...prevKmsVehicles,
							{
								kmsIni: prevKmsVehicles.length > 0 ? prevKmsVehicles[prevKmsVehicles.length - 1].kmsEnd : 0,
								kmsEnd: 0,
								inEmpty: false,
								kmsId:
									kmsVehicles[kmsVehicles.length - 1]?.kmsId && kmsVehicles[kmsVehicles.length - 1].kmsId! < 0
										? kmsVehicles[kmsVehicles.length - 1].kmsId
										: i * -1,
							},
						]
						copyOfServices[i] = {
							...prevService,
							kmsVehicles: updatedKmsVehicles as KmsVehicle[],
						}
					} else {
						copyOfServices[i] = { ...timesheetApprovalDetailsAccept.services[i] }
					}
				}
				dispatch(acceptApprovalServices(copyOfServices))
			} else {
				const copyOfServices: Services[] = []

				for (let i = 0; i < timesheetPage.services.length; i++) {
					if (i === instance) {
						const prevServiceExecution = timesheetPage.services[i].serviceExecution
						const prevKmsVehicles =
							prevServiceExecution?.kmsVehicles && Array.isArray(prevServiceExecution.kmsVehicles)
								? prevServiceExecution.kmsVehicles
								: []
						const updatedKmsVehicles = [
							...prevKmsVehicles,
							{
								kmsIni: prevKmsVehicles.length > 0 ? prevKmsVehicles[prevKmsVehicles.length - 1].kmsEnd : 0,
								kmsEnd: 0,
								inEmpty: false,
								kmsId:
									kmsVehicles[kmsVehicles.length - 1]?.kmsId && kmsVehicles[kmsVehicles.length - 1].kmsId! < 0
										? kmsVehicles[kmsVehicles.length - 1].kmsId
										: i * -1,
							},
						]
						// Only update kmsVehicles, keep other fields untouched
						copyOfServices.push({
							...timesheetPage.services[i],
							serviceExecution: {
								...(prevServiceExecution as ServiceExecution),
								kmsVehicles: updatedKmsVehicles as KmsVehicle[],
							},
						})
					} else {
						copyOfServices.push({
							...timesheetPage.services[i],
							serviceExecution: timesheetPage.services[i].serviceExecution as ServiceExecution,
						})
					}
				}
				dispatch(handleTimesheetServicesDetails(copyOfServices))
			}

			dispatch(hasChanges({ hasChangesFromReviewer: true }))
		}
	}

	/**
	 * Handler to remove a KmsVehicle entry based on its kmsId. It updates both the local state and the global state in Redux.
	 * The handler first checks if the provided kmsId is valid. If it is, it creates a copy of the current services from the Redux store,
	 * iterates through them to find and remove the KmsVehicle with the matching kmsId, and then updates the Redux store with the modified services.
	 * Finally, it updates the local state to remove the KmsVehicle from the UI and dispatches an action to indicate that changes have been made.
	 */
	function handleRemoveKmsVehicle(kmsId: number | undefined) {
		if (kmsId === undefined || !kmsId) return

		const copyOfServices = [...timesheetApprovalDetailsAccept.services]
		for (let i = 0; i < copyOfServices.length; i++) {
			const service = copyOfServices[i]
			if (service.kmsVehicles && service.kmsVehicles.length > 0) {
				const updatedKmsVehiclesForService = service.kmsVehicles.filter(item => item.kmsId !== kmsId)
				copyOfServices[i] = {
					...service,
					kmsVehicles: updatedKmsVehiclesForService,
				}
			} else {
				copyOfServices[i] = { ...service }
			}
		}
		dispatch(acceptApprovalServices(copyOfServices))

		const updatedServices = timesheetPage.services.map(service => {
			if (service?.serviceExecution?.kmsVehicles && service?.serviceExecution?.kmsVehicles?.length > 0) {
				const updatedKmsVehiclesForService = service?.serviceExecution?.kmsVehicles.filter(
					(item: KmsVehicle) => item.kmsId !== kmsId,
				)
				return {
					...service,
					serviceExecution: {
						...service.serviceExecution,
						kmsVehicles: updatedKmsVehiclesForService,
					},
				}
			} else {
				return { ...service }
			}
		})

		dispatch(handleTimesheetServicesDetails(updatedServices))

		setKmsVehicles(prevKmsVehicles => prevKmsVehicles!.filter(item => item.kmsId !== kmsId))

		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	/**
	 * Handler to update the kmsIni value of a specific KmsVehicle entry. It takes the index of the KmsVehicle in the list and the new value as parameters.
	 * The handler updates the local state by mapping through the existing kmsVehicles and updating the kmsIni value of the matching entry.
	 * It then checks if the timesheet is approved. If it is, it creates a copy of the services from the approval details in Redux, updates the corresponding KmsVehicle entry in the service at the current instance index, and dispatches an action to update the approval services in Redux.
	 * If the timesheet is not approved, it performs a similar update on the services from the timesheet page in Redux and dispatches an action to update the timesheet services details.
	 * Finally, it dispatches an action to indicate that changes have been made.
	 */
	function handleKmsIniChange(idx: number, value: number) {
		setKmsVehicles(prev => {
			const updated = prev.map((kmv, i) => (i === idx ? { ...kmv, kmsIni: value } : kmv))
			return updated
		})

		if (isTimeSheetApproved) {
			const copyOfServices = [...timesheetApprovalDetailsAccept.services]
			for (let i = 0; i < copyOfServices.length; i++) {
				if (i === instance) {
					const prevService = timesheetApprovalDetailsAccept.services[i]
					const prevKmsVehicles = prevService?.kmsVehicles || []
					const updatedKmsVehicles = prevKmsVehicles.map((kmv, j) => (j === idx ? { ...kmv, kmsIni: value } : kmv))
					copyOfServices[i] = {
						...prevService,
						kmsVehicles: updatedKmsVehicles as KmsVehicle[],
					}
				} else {
					copyOfServices[i] = { ...timesheetApprovalDetailsAccept.services[i] }
				}
			}
			dispatch(acceptApprovalServices(copyOfServices))
		} else {
			const copyOfServices: {
				service: ServiceOriginal
				serviceExecution:
					| (ServiceExecution & {
							kmsVehicles: KmsVehicle[]
					  })
					| ServiceExecution
			}[] = []
			for (let i = 0; i < timesheetPage.services.length; i++) {
				if (i === instance) {
					const prevServiceExecution = timesheetPage.services[i].serviceExecution
					const prevKmsVehicles =
						prevServiceExecution?.kmsVehicles && Array.isArray(prevServiceExecution.kmsVehicles)
							? prevServiceExecution.kmsVehicles
							: []
					const updatedKmsVehicles = prevKmsVehicles.map((kmv, j) => (j === idx ? { ...kmv, kmsIni: value } : kmv))
					const copyOFServiceExecution = {
						...prevServiceExecution,
						kmsVehicles: updatedKmsVehicles as KmsVehicle[],
					}
					copyOfServices.push({
						service: timesheetPage.services[i].service,
						serviceExecution: copyOFServiceExecution as ServiceExecution & {
							kmsVehicles: KmsVehicle[]
						},
					})
				} else {
					copyOfServices.push({
						service: timesheetPage.services[i].service,
						serviceExecution: timesheetPage.services[i].serviceExecution as ServiceExecution,
					})
				}
			}
			dispatch(handleTimesheetServicesDetails(copyOfServices))
		}
		// update serviceExeution in Redux state to reflect changes immediately in the UI
		dispatch(
			handleTimesheetServicesDetails(
				timesheetPage.services.map((service, i) =>
					i === instance
						? {
								...service,
								serviceExecution: {
									...service.serviceExecution,
									kmsVehicles: service.serviceExecution?.kmsVehicles?.map((kmv, j) =>
										j === idx ? { ...kmv, kmsIni: value } : kmv,
									) as KmsVehicle[],
								} as ServiceExecution,
							}
						: service,
				),
			),
		)

		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	/**
	 * Handler to update the kmsEnd value of a specific KmsVehicle entry. It takes the index of the KmsVehicle in the list and the new value as parameters.
	 * The handler updates the local state by mapping through the existing kmsVehicles and updating the kmsEnd value of the matching entry.
	 * It then checks if the timesheet is approved. If it is, it creates a copy of the services from the approval details in Redux, updates the corresponding KmsVehicle entry in the service at the current instance index, and dispatches an action to update the approval services in Redux.
	 * If the timesheet is not approved, it performs a similar update on the services from the timesheet page in Redux and dispatches an action to update the timesheet services details.
	 * Finally, it dispatches an action to indicate that changes have been made.
	 */
	function handleKmsEndChange(idx: number, value: number) {
		setKmsVehicles(prev => {
			const updated = prev.map((kmv, i) => (i === idx ? { ...kmv, kmsEnd: value } : kmv))
			return updated
		})

		if (isTimeSheetApproved) {
			const copyOfServices = [...timesheetApprovalDetailsAccept.services]
			for (let i = 0; i < copyOfServices.length; i++) {
				if (i === instance) {
					const prevService = timesheetApprovalDetailsAccept.services[i]
					const prevKmsVehicles = prevService?.kmsVehicles || []
					const updatedKmsVehicles = prevKmsVehicles.map((kmv, j) => (j === idx ? { ...kmv, kmsEnd: value } : kmv))
					copyOfServices[i] = {
						...prevService,
						kmsVehicles: updatedKmsVehicles as KmsVehicle[],
					}
				} else {
					copyOfServices[i] = { ...timesheetApprovalDetailsAccept.services[i] }
				}
			}
			dispatch(acceptApprovalServices(copyOfServices))
		} else {
			const copyOfServices: {
				service: ServiceOriginal
				serviceExecution:
					| (ServiceExecution & {
							kmsVehicles: KmsVehicle[]
					  })
					| ServiceExecution
			}[] = []
			for (let i = 0; i < timesheetPage.services.length; i++) {
				if (i === instance) {
					const prevServiceExecution = timesheetPage.services[i].serviceExecution
					const prevKmsVehicles =
						prevServiceExecution?.kmsVehicles && Array.isArray(prevServiceExecution.kmsVehicles)
							? prevServiceExecution.kmsVehicles
							: []
					const updatedKmsVehicles = prevKmsVehicles.map((kmv, j) => (j === idx ? { ...kmv, kmsEnd: value } : kmv))
					const copyOFServiceExecution = {
						...prevServiceExecution,
						kmsVehicles: updatedKmsVehicles as KmsVehicle[],
					}
					copyOfServices.push({
						service: timesheetPage.services[i].service,
						serviceExecution: copyOFServiceExecution as ServiceExecution & {
							kmsVehicles: KmsVehicle[]
						},
					})
				} else {
					copyOfServices.push({
						service: timesheetPage.services[i].service,
						serviceExecution: timesheetPage.services[i].serviceExecution as ServiceExecution,
					})
				}
			}
			dispatch(handleTimesheetServicesDetails(copyOfServices))
		}

		// update serviceExeution in Redux state to reflect changes immediately in the UI
		dispatch(
			handleTimesheetServicesDetails(
				timesheetPage.services.map((service, i) =>
					i === instance
						? {
								...service,
								serviceExecution: {
									...service.serviceExecution,
									kmsVehicles: service.serviceExecution?.kmsVehicles?.map((kmv, j) =>
										j === idx ? { ...kmv, kmsEnd: value } : kmv,
									) as KmsVehicle[],
								} as ServiceExecution,
							}
						: service,
				),
			),
		)

		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	/**
	 * Handler to update the inEmpty value of a specific KmsVehicle entry. It takes the index of the KmsVehicle in the list and the new value as parameters.
	 * The handler updates the local state by mapping through the existing kmsVehicles and updating the inEmpty value of the matching entry.
	 * It then checks if the timesheet is approved. If it is, it creates a copy of the services from the approval details in Redux, updates the corresponding KmsVehicle entry in the service at the current instance index, and dispatches an action to update the approval services in Redux.
	 * If the timesheet is not approved, it performs a similar update on the services from the timesheet page in Redux and dispatches an action to update the timesheet services details.
	 * Finally, it dispatches an action to indicate that changes have been made.
	 */
	function handleInEmptyChange(idx: number, value: boolean) {
		setKmsVehicles(prev => {
			const updated = prev.map((kmv, i) => (i === idx ? { ...kmv, inEmpty: value } : kmv))
			return updated
		})

		if (isTimeSheetApproved) {
			const copyOfServices = [...timesheetApprovalDetailsAccept.services]
			for (let i = 0; i < copyOfServices.length; i++) {
				if (i === instance) {
					const prevService = timesheetApprovalDetailsAccept.services[i]
					const prevKmsVehicles = prevService?.kmsVehicles || []
					const updatedKmsVehicles = prevKmsVehicles.map((kmv, j) => (j === idx ? { ...kmv, inEmpty: value } : kmv))
					copyOfServices[i] = {
						...prevService,
						kmsVehicles: updatedKmsVehicles as KmsVehicle[],
					}
				} else {
					copyOfServices[i] = { ...timesheetApprovalDetailsAccept.services[i] }
				}
			}
			dispatch(acceptApprovalServices(copyOfServices))
		} else {
			const copyOfServices: {
				service: ServiceOriginal
				serviceExecution:
					| (ServiceExecution & {
							kmsVehicles: KmsVehicle[]
					  })
					| ServiceExecution
			}[] = []
			for (let i = 0; i < timesheetPage.services.length; i++) {
				if (i === instance) {
					const prevServiceExecution = timesheetPage.services[i].serviceExecution
					const prevKmsVehicles =
						prevServiceExecution?.kmsVehicles && Array.isArray(prevServiceExecution.kmsVehicles)
							? prevServiceExecution.kmsVehicles
							: []
					const updatedKmsVehicles = prevKmsVehicles.map((kmv, j) => (j === idx ? { ...kmv, inEmpty: value } : kmv))
					const copyOFServiceExecution = {
						...prevServiceExecution,
						kmsVehicles: updatedKmsVehicles as KmsVehicle[],
					}
					copyOfServices.push({
						service: timesheetPage.services[i].service,
						serviceExecution: copyOFServiceExecution as ServiceExecution & {
							kmsVehicles: KmsVehicle[]
						},
					})
				} else {
					copyOfServices.push({
						service: timesheetPage.services[i].service,
						serviceExecution: timesheetPage.services[i].serviceExecution as ServiceExecution,
					})
				}
			}
			dispatch(handleTimesheetServicesDetails(copyOfServices))
		}

		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	useEffect(() => {
		if (serviceDetail?.serviceExecution === null) {
			setKmsVehicles(serviceDetail.service.kmsVehicles as KmsVehicle[])
		} else if (
			serviceDetail?.serviceExecution?.kmsVehicles &&
			Array.isArray(serviceDetail.serviceExecution.kmsVehicles)
		) {
			setKmsVehicles(serviceDetail.serviceExecution.kmsVehicles as KmsVehicle[])
		}
	}, [serviceDetail?.serviceExecution, serviceDetail?.service.kmsVehicles])

	return (
		<Paper
			sx={{
				padding: 3,
				alignSelf: 'stretch',
				boxShadow: 'none',
				borderRadius: 0,
			}}
		>
			<Typography
				variant='subtitle2'
				sx={{
					fontFamily: 'var(--font-family)',
					fontWeight: 600,
					fontSize: '0.875rem',
					lineHeight: '157%',
					color: 'var(--primary-dark)',
					paddingBottom: 1,
				}}
			>
				Informações do Serviço
			</Typography>
			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell sx={headerTableCellStyle}>Cliente</TableCell>
						<TableCell sx={headerTableCellStyle}>ID Serviço</TableCell>
						<TableCell sx={headerTableCellStyle}>Viatura</TableCell>
						<TableCell sx={headerTableCellStyle}>Passageiros (Escalado)</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell sx={bodyTableCellStyle}>{serviceDetail.service.clientName}</TableCell>
						<TableCell sx={bodyTableCellStyle}>{serviceDetail.service.serviceId}</TableCell>
						<TableCell sx={bodyTableCellStyle}>{serviceDetail.service.vehicle.fleetNumber}</TableCell>
						<TableCell sx={bodyTableCellStyle}>{serviceDetail.service.pax}</TableCell>
					</TableRow>
				</TableBody>
			</Table>
			{(serviceDetail?.service?.comments?.trim().length ?? 0) > 0 && (
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell sx={headerTableCellStyle}>Instruções Especiais</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{specialInstructions
							// TODO: Clarify the separator for multiple comments if it's different
							?.split(' / ') // Assuming comments are separated by '/' for multiple instructions, adjust as needed
							.map((comment: string, index: number) => (
								<TableRow key={index}>
									<TableCell sx={bodyTableCellStyle}>{capitalizeComment(comment)}</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			)}

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					paddingTop: 2,
					paddingBottom: 1,
					alignSelf: 'stretch',
				}}
			>
				<Typography
					variant='subtitle2'
					sx={{
						fontFamily: 'var(--font-family)',
						fontWeight: 600,
						fontSize: '0.875rem',
						lineHeight: '157%',
						color: 'var(--primary-dark)',
					}}
				>
					Registo de Kms Efetuados
				</Typography>
				<CustomButton
					size='small'
					variant='text'
					sx={{ marginLeft: 'auto' }}
					endIcon={
						<AddIcon
							fontSize='small'
							color='inherit'
						/>
					}
					onClick={() => handleAddKmsVehicle()}
				>
					Adicionar Registo
				</CustomButton>
			</Box>

			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell sx={headerTableCellStyle}>Registo</TableCell>
						<TableCell sx={headerTableCellStyle}>Kms Início</TableCell>
						<TableCell sx={headerTableCellStyle}>Kms Fim</TableCell>
						<TableCell
							colSpan={2}
							sx={headerTableCellStyle}
						>
							Vazio
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{kmsVehicles?.map((kmv, idx) => (
						<KmsVehicleRow
							kmv={kmv}
							key={kmv.kmsId ?? idx}
							idx={idx}
							kmsVehicles={kmsVehicles}
							isSecondaryDriverService={isTowDriversService && isSecondaryDriverService}
							removeKmsVehicleHandler={handleRemoveKmsVehicle}
							onKmsIniChange={handleKmsIniChange}
							onKmsEndChange={handleKmsEndChange}
							onInEmptyChange={handleInEmptyChange}
						/>
					))}
				</TableBody>
			</Table>
			<Table sx={{ marginTop: '1rem' }}>
				<TableRow>
					<TableCell
						colSpan={5}
						sx={{
							border: 'none',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 2,
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontSize: '0.75rem',
									fontWeight: 600,
									lineHeight: '130%',
									color: 'var(--primary-dark)',
								}}
							>
								Totais de Kms
							</Typography>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 2,
								}}
							>
								<CustomTextField
									aria-label='Kms registry when transporting passengers'
									disabled
									label='Kms Carga'
									size='small'
									type='number'
									value={kmsNotEmpty}
								/>
								<CustomTextField
									disabled
									label='Kms Vazio'
									aria-label='Kms registry when not transporting passengers'
									size='small'
									type='number'
									value={kmsEmpty}
								/>
								<CustomTextField
									disabled
									label='Total Kms'
									aria-label='Total Kms'
									size='small'
									type='number'
									value={kmsEmpty + kmsNotEmpty}
								/>
							</Box>
						</Box>
					</TableCell>
				</TableRow>
			</Table>
			{/* Notas Adicionais */}
			<PresentationalNotesAndComments
				type='element'
				title='Notas Adicionais'
				label='Notas Adicionais'
				content={serviceNotes}
			/>
		</Paper>
	)
}
